import {
    buildAdminSingleLogoutUrl,
    buildKeycloakLogoutUrl,
    isKeycloakAuthEnabled,
    normalizeOidcRedirectUri,
} from "../configs/keycloakAuth";

export const PORTAL_KEYCLOAK_LOGOUT_FLAG = "portal_keycloak_logout";
const PKCE_ID_TOKEN_STORAGE_KEY = "ROCP_idToken";

/**
 * Ends the Keycloak SSO session. Keep tokens until return — clearing them
 * first lets autoLogin bounce back into SSO. post_logout_redirect_uri must
 * be the login origin; a query string is rejected by Keycloak.
 */
export function markPortalKeycloakLogout(): void {
    sessionStorage.setItem(PORTAL_KEYCLOAK_LOGOUT_FLAG, "1");
}

export function clearPortalKeycloakLogoutFlag(): void {
    sessionStorage.removeItem(PORTAL_KEYCLOAK_LOGOUT_FLAG);
}

export function isPortalKeycloakLogoutInProgress(): boolean {
    if (typeof window === "undefined") {
        return false;
    }

    if (sessionStorage.getItem(PORTAL_KEYCLOAK_LOGOUT_FLAG) === "1") {
        return true;
    }

    return (
        new URLSearchParams(window.location.search).get("portal_web_logout") ===
        "1"
    );
}

const PKCE_STORAGE_PREFIX = "ROCP_";

function clearPkceBrowserStorage(): void {
    const keys: string[] = [];

    for (let index = 0; index < localStorage.length; index += 1) {
        const key = localStorage.key(index);
        if (key?.startsWith(PKCE_STORAGE_PREFIX)) {
            keys.push(key);
        }
    }

    keys.forEach((key) => localStorage.removeItem(key));
}

function storedLoginInProgress(): boolean {
    const raw = localStorage.getItem(`${PKCE_STORAGE_PREFIX}loginInProgress`);

    if (!raw) {
        return false;
    }

    try {
        return JSON.parse(raw) === true;
    } catch {
        return raw === "true";
    }
}

function stripPortalAuthQueryParams(): void {
    const params = new URLSearchParams(window.location.search);
    params.delete("portal_web_logout");
    params.delete("from_portal_sync");
    const query = params.toString();
    window.history.replaceState(
        null,
        "",
        `${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`,
    );
}

/**
 * Clear PKCE storage before AuthProvider mounts (logout return, stale
 * loginInProgress, or Laravel from_portal_sync).
 */
export function recoverPortalAuthStorage(): void {
    if (typeof window === "undefined") {
        return;
    }

    const params = new URLSearchParams(window.location.search);
    const loggingOut = isPortalKeycloakLogoutInProgress();
    const staleLogin = storedLoginInProgress() && !params.get("code");
    const portalSync = params.get("from_portal_sync") === "1";

    if (!loggingOut && !staleLogin && !portalSync) {
        return;
    }

    clearPkceBrowserStorage();
    clearPortalKeycloakLogoutFlag();
    stripPortalAuthQueryParams();
}

export function resolveStoredIdToken(idToken?: string): string | undefined {
    if (idToken) {
        return idToken;
    }

    if (typeof window === "undefined") {
        return undefined;
    }

    const raw = localStorage.getItem(PKCE_ID_TOKEN_STORAGE_KEY);
    if (!raw) {
        return undefined;
    }

    try {
        const parsed: unknown = JSON.parse(raw);
        return typeof parsed === "string" && parsed.length > 0
            ? parsed
            : undefined;
    } catch {
        return raw.length > 0 ? raw : undefined;
    }
}

export function redirectToPortalWebLogout(
    idToken?: string,
    redirect: (url: string) => void = (url) => window.location.replace(url),
): void {
    if (!isKeycloakAuthEnabled(import.meta.env.VITE_AUTH_PROVIDER)) {
        const base =
            import.meta.env.VITE_AUTH_URL || import.meta.env.VITE_ADMIN_URL;

        if (!base) {
            console.error("VITE_AUTH_URL or VITE_ADMIN_URL must be set");
            return;
        }

        markPortalKeycloakLogout();
        redirect(`${base.replace(/\/$/, "")}/portal/logout`);
        return;
    }

    const authority = import.meta.env.VITE_KEYCLOAK_AUTHORITY;
    const clientId = import.meta.env.VITE_KEYCLOAK_CLIENT_ID;
    const portalUrl = import.meta.env.VITE_PORTAL_URL;
    const adminUrl =
        import.meta.env.VITE_ADMIN_URL || import.meta.env.VITE_AUTH_URL;

    if (!authority || !clientId || !portalUrl) {
        console.error(
            "VITE_KEYCLOAK_AUTHORITY, VITE_KEYCLOAK_CLIENT_ID and VITE_PORTAL_URL must be set",
        );
        return;
    }

    markPortalKeycloakLogout();

    const postLogoutRedirectUri = normalizeOidcRedirectUri(portalUrl);
    const resolvedIdToken = resolveStoredIdToken(idToken);

    if (adminUrl) {
        redirect(
            buildAdminSingleLogoutUrl({
                adminUrl,
                clientId,
                postLogoutRedirectUri,
                idToken: resolvedIdToken,
            }),
        );
        return;
    }

    redirect(
        buildKeycloakLogoutUrl({
            authority,
            clientId,
            postLogoutRedirectUri,
            idToken: resolvedIdToken,
        }),
    );
}
